/**
 * @file OrderChainStepper.tsx
 * @description Шкала стадий заказа над списком документов
 * (phase19.24-order-card-chain-design.md §5, §14).
 *
 * Правило блока на всю его жизнь: **степпер = стадии (F31), список = документы
 * (факты)**. Стадия без документа («Ожидание оплаты») существует только здесь.
 *
 * Цвет точки отвечает на один вопрос — чей ход: реестр владельцев
 * (`order-chain-owner.ts`) даёт заливку, контур и подпись. Ближайшая «ваша»
 * точка обведена зелёным, и до неё ведёт зелёный пунктир: менеджер видит, где
 * снова наступит его очередь, не читая текста.
 *
 * На 360px подписи под точками не помещаются — шкала скроллится, текущая
 * точка центрируется автоматически, а под шкалой остаётся одна строка.
 * Центрирование двигает ТОЛЬКО контейнер шкалы: прокрутка предков уводила
 * вправо всю страницу (дефект QA 2026-09-21).
 *
 * phase19.31 (приёмка qa4 п. 10): ширина точки подбирается под блок
 * (`useStepperFit`) — в окне целое число стадий, прокрутка шагом в точку,
 * край гаснет, если за ним есть ещё стадии. Первая стадия больше не режется
 * пополам на 1280.
 */

import { useEffect, useRef } from "react";

import { Connector, Dot } from "./OrderChainStepperDots";
import { snapScroll, useStepperFit } from "./use-stepper-fit";
import { OwnerChip } from "../OwnerChip";
import {
  isKnownStageKind,
  routeStageLabel,
  type OrderRouteDto,
  type OrderRouteStageDto,
} from "../../../lib/order-chain";
import { ownerMeta } from "../../../lib/order-chain-owner";
import { useScrollFade } from "../../ui/useScrollFade";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { cn } from "@/lib/utils";

/** Наименьшая ширина точки: с подписями (от 1024) и без них (телефон). */
const MIN_STEP_WIDE = 88;
const MIN_STEP_NARROW = 56;

interface Props {
  route: OrderRouteDto;
  /** Ближайшая стадия, где ход снова за менеджером (зелёный контур). */
  nextManager: OrderRouteStageDto | null;
}

export function OrderChainStepper({ route, nextManager }: Props) {
  const currentRef = useRef<HTMLLIElement>(null);
  // Ряд шкалы — он же ряд с затуханием края (видно, что шкала листается).
  // Узкое затухание: шаг прокрутки — целая точка, широкое гасило бы подпись.
  const fade = useScrollFade<HTMLOListElement>("1rem");
  const listRef = fade.ref;
  const stages = route.stages;
  const narrow = useMediaQuery(BELOW_LG_QUERY);
  const stepWidth = useStepperFit(listRef, stages.length, narrow ? MIN_STEP_NARROW : MIN_STEP_WIDE);
  const currentIndex = stages.findIndex((s) => s.state === "current");
  const nextIndex = nextManager ? stages.indexOf(nextManager) : -1;
  const current = currentIndex >= 0 ? stages[currentIndex] : null;

  useEffect(() => {
    const el = currentRef.current;
    const list = listRef.current;
    if (!el || !list) return;
    // Прокручиваем ТОЛЬКО свой контейнер. `scrollIntoView({inline:"center"})`
    // тянет за собой всех предков, включая корень документа: на 360px
    // страница уезжала вправо (замер ui-visual-qa 2026-09-21 — html.scrollWidth
    // 517 при ширине 360 у заказов с 7–8 стадиями).
    const centered = el.offsetLeft - list.clientWidth / 2 + el.offsetWidth / 2;
    const max = Math.max(0, list.scrollWidth - list.clientWidth);
    // Шагом в точку: у левого края — целая стадия, а не половина подписи.
    const left = snapScroll(Math.max(0, Math.min(centered, max)), stepWidth, max);
    // В jsdom `scrollTo` у элемента нет — присваиваем позицию напрямую.
    if (typeof list.scrollTo === "function") {
      list.scrollTo({ left, behavior: "smooth" });
    } else {
      list.scrollLeft = left;
    }
  }, [currentIndex, stepWidth, listRef]);

  if (stages.length === 0) return null;

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <ol
        ref={listRef}
        onScroll={fade.update}
        style={fade.style}
        // Ручная прокрутка пальцем тоже останавливается на целой стадии.
        className="flex w-full min-w-0 max-w-full snap-x snap-proximity items-start overflow-x-auto pb-1"
      >
        {stages.map((stage, index) => {
          // Заглушка маршрута («Получение», способ получения не выбран)
          // рисуется нейтрально — как ход системы: обещать ход склада или
          // клиента по неизвестной точке нельзя.
          const placeholder = !isKnownStageKind(stage.kind);
          const meta = ownerMeta(placeholder ? "SYSTEM" : stage.owner);
          const isCurrent = stage.state === "current";
          const isNextMine = index === nextIndex;
          return (
            <li
              key={`${stage.kind}-${index}`}
              ref={isCurrent ? currentRef : undefined}
              // `relative` обязателен: подпись для скринридера внутри —
              // `.sr-only` (position: absolute). Без позиционированного
              // предка её содержащий блок — корень документа, она выпадает
              // из скроллящейся шкалы и растягивает страницу вправо
              // (на 360px html.scrollWidth был 773 при ширине 360).
              className={cn("relative flex shrink-0 snap-start flex-col items-center gap-1", stepWidth ? null : "w-16 lg:w-24")}
              style={stepWidth ? { width: stepWidth } : undefined}
              // Подсказка точки приходит с сервера («Способ получения выберут
              // при согласовании») — свою не придумываем.
              title={stage.hint ?? undefined}
            >
              <div className="flex w-full items-center">
                <Connector
                  visible={index > 0}
                  passed={index <= currentIndex}
                  toMine={currentIndex >= 0 && index > currentIndex && index <= nextIndex}
                />
                <Dot
                  stage={stage}
                  isCurrent={isCurrent}
                  isNextMine={isNextMine}
                  placeholder={placeholder}
                />
                <Connector
                  visible={index < stages.length - 1}
                  passed={index < currentIndex}
                  toMine={currentIndex >= 0 && index >= currentIndex && index < nextIndex}
                />
              </div>
              {/* Подписи — только на desktop: на 360px они не читаются. */}
              <div className="hidden w-full flex-col items-center gap-1 text-center lg:flex">
                <span
                  className={cn(
                    "line-clamp-2 text-xs",
                    isCurrent ? "font-medium text-on-surface" : "text-on-surface-variant",
                  )}
                >
                  {routeStageLabel(stage)}
                </span>
                <OwnerChip
                  owner={placeholder ? "SYSTEM" : stage.owner}
                  short
                  className={cn(!isCurrent && "opacity-80")}
                />
                {placeholder && stage.hint ? (
                  <span className="line-clamp-2 text-xs text-on-surface-muted">
                    {stage.hint}
                  </span>
                ) : null}
                {isNextMine ? (
                  <span className="text-xs text-primary">ваш след. шаг</span>
                ) : null}
                {isCurrent ? <span className="text-xs text-primary">вы здесь</span> : null}
              </div>
              <span className="sr-only">
                {routeStageLabel(stage)} — {meta.aria}
              </span>
            </li>
          );
        })}
      </ol>

      {/* Одна строка под шкалой вместо подписей — правило 360px из §14. */}
      <p className="flex flex-wrap items-center gap-2 text-xs text-on-surface-variant lg:hidden">
        {current ? (
          <>
            <span className="font-medium text-on-surface">{routeStageLabel(current)}</span>
            <span>· вы здесь</span>
            <OwnerChip
              owner={isKnownStageKind(current.kind) ? current.owner : "SYSTEM"}
            />
            {/* Подсказка текущей точки — вторая строка на 360px: на телефоне
                подписей под точками нет, и заглушку иначе не объяснить. */}
            {current.hint ? (
              <span className="w-full text-on-surface-muted">{current.hint}</span>
            ) : null}
          </>
        ) : null}
        {nextManager ? (
          <span className="text-primary">дальше: {routeStageLabel(nextManager)} (вы)</span>
        ) : null}
      </p>
    </div>
  );
}
